/**
 * A panel that slides in from the right and OVERLAYS.
 *
 * One of TWO dialog shells, not the only one — `Modal.tsx` is the centred one. They share
 * every behaviour that makes a dialog correct (focus return, the Tab trap, Escape, the scrim
 * press, the unsaved-work guard) through `useDialog`, which is where the reasoning for each of
 * those now lives. What is left here is the only thing a drawer decides for itself: that it
 * comes from the right edge and stops short of the full width.
 *
 * WHICH TO REACH FOR. A drawer is for work that belongs BESIDE the page — a form whose result
 * changes the list behind it, a record you are editing in context. A modal is for a view that
 * REPLACES attention for a moment and returns it: a full ranked list you scan and dismiss. The
 * drawer's width is what makes that distinction readable, so a drawer that fills the viewport
 * has become a modal with extra steps.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT OVERLAYS AND DOES NOT PUSH
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The push-inward kind — the page squeezing left to make room — was asked for explicitly and
 * explicitly refused, because three of this product's most expensive surfaces do not reflow when
 * their container changes width and nothing tells them to:
 *
 *   Highcharts        sized once, on `chart.render`; a squeezed container leaves the plot drawn
 *                     at the old width with its right-hand series clipped
 *   the SLD canvas    lays out in a fixed viewBox and re-fits on an explicit call
 *   MapLibre          needs `map.resize()`; without it the canvas keeps its old pixel size and
 *                     the basemap tears at the new edge
 *
 * A drawer that resizes the page therefore breaks the three screens an operator is most likely
 * to open one from. Overlaying costs nothing and breaks nothing.
 *
 * For the same reason THE BODY SCROLL IS NOT LOCKED. `overflow: hidden` on `<body>` is the usual
 * reflex and it is the push-inward bug wearing a different hat: removing the scrollbar widens the
 * page underneath by its own width, every chart on it is then drawn at the wrong size, and the
 * whole layout jumps sideways as the drawer opens. The scrim swallows the clicks; the wheel over
 * the page beneath still scrolls it, which is the lesser of the two faults and the one nobody has
 * ever complained about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OPAQUE. THE GLASS IS RETIRED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `var(--card)` and a solid `--border` edge, never a translucent panel: those measured 1.23:1 on
 * their edges against a 3:1 floor, and a dialog is the one surface where the edge is the only
 * thing saying where the page stops and the form starts. The scrim is the only translucency here
 * and it is 42% black, matching every drawer this replaced.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS STILL A DIALOG, SO IT KEEPS A DIALOG'S CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Focus moves in on open, is TRAPPED while open, and is restored to whatever opened it on
 * close; a dismiss with typed work in the panel asks before it destroys it. All of that is
 * `useDialog`'s, which carries the incident behind each one. A caller that can hold typed work
 * passes `dirty` and gets the guard; everything else is automatic.
 *
 * The guard is deliberately NOT `window.confirm`: it is unstyled, it is not screen-reader-
 * announced in the dialog's own context, and on this product it would be the only browser
 * chrome in the console.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `modal={false}`: A PANEL BESIDE A PAGE THAT STAYS IN USE (2026-10-13)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The work order drawer (Hamzah: "make it same in side drawer", the reference product's edit
 * panel) sits over the right of a board the reader keeps working: another card is pressed and
 * the drawer turns to that order, without closing and sliding in again. A scrim would swallow
 * that press, so this mode draws NO scrim, lets the page behind take clicks, does not trap Tab,
 * starts below the console header (the bell and the site picker stay in reach), and returns focus
 * to the last thing touched outside it (`useDialog`'s `modal`). Everything else
 * is the same panel: same edge, same header, same guard. Default stays modal — a form whose
 * result is not on the page behind has no reason to leave that page live.
 */
import { type ReactNode } from 'react';
import { Icon } from './Icon';
import { DrawerCloseContext } from './drawer-close';
import { useDialog } from './use-dialog';

export function Drawer({
  title,
  desc,
  onClose,
  width = 560,
  dirty = false,
  discardLabel = 'Discard changes',
  modal = true,
  chrome,
  footer,
  children,
}: {
  /**
   * False: no scrim, the page behind stays usable, no Tab trap (see the docblock). The width is
   * then also kept clear of the rail, so the page's navigation is never covered.
   */
  modal?: boolean;
  title: ReactNode;
  /** One line under the title, saying what this panel decides. Optional; most have one. */
  desc?: ReactNode;
  /** Close for real. The guard runs before this, never after. */
  onClose: () => void;
  /**
   * The panel's width in pixels, capped at the viewport so a phone gets a full-width sheet.
   *
   * 560 by default: wide enough for a two-up field row at the `sm` breakpoint and narrow enough
   * that the page behind stays recognisable, which is half of what makes a drawer readable as
   * "over" rather than "instead of".
   */
  width?: number;
  /** There is typed work in here that a dismiss would destroy. */
  dirty?: boolean;
  /** What the destructive button says, when the wording "changes" is wrong for this panel. */
  discardLabel?: string;
  /**
   * A strip between the header and the scroller — tabs, a filter, anything that must stay put
   * while the body moves.
   *
   * IT IS A SLOT AND NOT A `sticky` CHILD, which is what the organisation form tried first. A
   * `position: sticky` first child has to cancel the scroller's own padding to sit flush, and
   * the negative margins that do it are measured against the CONTENT box while `top` is measured
   * against the SCROLLPORT — so the strip settles a padding's width low and the form scrolls
   * through the gap above it. Out here there is no gap to leave: the flex column places it, the
   * scroller starts under it, and nothing can be painted over it because nothing overlaps it.
   *
   * The caller owns its own padding, border and `shrink-0`; this only decides where it sits.
   */
  chrome?: ReactNode;
  /** Actions, pinned to the bottom. Anything in here may call `useDrawerClose()`. */
  footer?: ReactNode;
  children: ReactNode;
}): ReactNode {
  /*
   * Focus return, the Tab trap, Escape, the scrim press and the unsaved-work guard all live in
   * `useDialog`, shared with `Modal`. They were written here first and the reasoning for each
   * is on that hook. Nothing about them is drawer-specific: what a drawer decides is that it
   * arrives from the right and stops short of the full width, which is the style block below.
   */
  const { panel, shown, requestClose, asking, setAsking, onKeyDown, onScrimMouseDown, onScrimClick } =
    useDialog({ onClose, dirty, modal });

  return (
    <DrawerCloseContext.Provider value={requestClose}>
      <div
        /* Non-modal, it starts BELOW the console header: the bell, the site picker and the
           appearance switch belong to the page that stays in use, and the bell is one of the ways
           a notice opens an order into this very panel. */
        className={
          modal
            ? 'fixed inset-0 z-50 flex justify-end'
            : 'pointer-events-none fixed inset-x-0 top-[var(--header-h)] bottom-0 z-40 flex justify-end'
        }
        style={
          modal
            ? {
                background: 'rgb(0 0 0 / 42%)',
                opacity: shown ? 1 : 0,
                transition: 'opacity var(--dur-drawer) var(--ease-drawer)',
              }
            : undefined
        }
        onMouseDown={modal ? onScrimMouseDown : undefined}
        onClick={modal ? onScrimClick : undefined}
      >
        <aside
          ref={panel}
          tabIndex={-1}
          role="dialog"
          aria-modal={modal ? 'true' : 'false'}
          aria-label={typeof title === 'string' ? title : undefined}
          className={
            modal
              ? 'flex h-full w-full flex-col outline-none'
              : 'pointer-events-auto flex h-full w-full flex-col shadow-[-12px_0_32px_rgb(0_0_0/0.14)] outline-none'
          }
          style={{
            /* Beside a live page, never over its rail: the page's navigation stays in reach. On a
               window too narrow for that, the panel takes the width it can, as a sheet. */
            maxWidth: modal
              ? width
              : `min(${width}px, max(calc(100vw - var(--sidebar-w)), min(100vw, 420px)))`,
            background: 'var(--card)',
            borderLeft: '1px solid var(--border)',
            transform: shown ? 'none' : 'translateX(100%)',
            transition: 'transform var(--dur-drawer) var(--ease-drawer)',
          }}
          /* Belt and braces with the press check above: this one stops a click INSIDE the panel
             from reaching the scrim's handler at all, which keeps the dismiss logic readable as
             "the scrim was pressed and released" rather than as a chain of negations. */
          onClick={(e) => e.stopPropagation()}
          onKeyDown={modal ? onKeyDown : undefined}
        >
          <header
            className="flex shrink-0 items-start justify-between gap-[10px] border-b p-[14px_18px]"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="min-w-0">
              <div className="lm-t-base font-bold">{title}</div>
              {desc !== undefined && (
                <div className="lm-t-sm mt-[2px]" style={{ color: 'var(--muted-fg)' }}>
                  {desc}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="lm-hov flex size-[26px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] border"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-fg)' }}
            >
              <Icon name="ic-x" size={13} />
            </button>
          </header>

          {chrome}

          {/* The one scroller. A drawer with two of them is a drawer where half the form is
              unreachable on a short window, which is how a fixed-height field group hides a
              Save button. */}
          <div className="flex min-h-0 flex-1 flex-col gap-[var(--gap-block)] overflow-y-auto p-[16px_18px]">
            {children}
          </div>

          {/*
            THE GUARD, ABOVE THE ACTIONS RATHER THAN OVER THEM.

            Pushing the footer down instead of covering it keeps both answers visible at once —
            an overlay that hides the Save button turns "are you sure" into "where did my save
            go". `role="alert"` because the reader's attention is on the control they just
            pressed, and a strip appearing elsewhere announces nothing by itself.
          */}
          {asking && (
            <div
              role="alert"
              className="flex shrink-0 flex-wrap items-center justify-between gap-[8px] border-t p-[10px_18px]"
              style={{
                borderColor: 'rgb(var(--warn-rgb) / 35%)',
                background: 'rgb(var(--warn-rgb) / 10%)',
              }}
            >
              <span className="lm-t-sm min-w-0 flex-1 leading-[1.45]">
                You have typed something that is not saved. Closing loses it.
              </span>
              <button
                type="button"
                autoFocus
                onClick={() => setAsking(false)}
                className="lm-hov shrink-0 rounded-[var(--radius-sm)] border px-[11px] py-[5px] text-[12px] font-semibold"
                style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={onClose}
                className="lm-hov shrink-0 rounded-[var(--radius-sm)] px-[11px] py-[5px] text-[12px] font-semibold"
                style={{ background: 'var(--danger)', color: '#fff' }}
              >
                {discardLabel}
              </button>
            </div>
          )}

          {footer !== undefined && (
            <footer
              className="flex shrink-0 items-center justify-between gap-[10px] border-t p-[11px_18px]"
              style={{ borderColor: 'var(--border)' }}
            >
              {footer}
            </footer>
          )}
        </aside>
      </div>
    </DrawerCloseContext.Provider>
  );
}
